import React, {useState} from 'react';
import { Form, Formik, ErrorMessage } from "formik";
import { Button, Row, Col, Label } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { CreateEmailCampaignSchema } from '../../../../helpers/schemas';
import { NOTIFICATION_CREATE_EMAIL_CAMPAIGN } from '../../../../config/notifications';
import { createEmailCampaign as createEmailCampaignApi} from "../../../../api/emailCampaigns/emailCampaigns";
import { EMAIL_CAMPAIGN_TEMPLATES, EMAIL_CAMPAIGN_RECIPIENTS, EMAIL_CAMPAIGN_RECIPIENTS_TYPES, ACL } from '../../../../config/constants';
import { SelectField } from '../../../shared/SelectField';
import { TextField } from '../../../../components/shared/TextField';
import { FieldMessages } from "../../../layout/Messages/FieldMessages";
import { HTTP_CREATED } from '../../../../config/constants/requestsStatuses';
import { store } from "../../../../store/store";
import { NOTIFICATION_ERROR } from '../../../../store/actions/types';
import { getUsers as getUsersApi } from '../../../../api/clients/users';
import { UserMultiSelect } from '../../../../components/shared/UserMultiSelect';

function CreateEmailCampaignForm(props) {
  const initialValues = {
    template: EMAIL_CAMPAIGN_TEMPLATES.TERMS_AND_POLICY_UPDATE.value,
    recipients_type: EMAIL_CAMPAIGN_RECIPIENTS_TYPES.TEST_USERS,
    recipient_test_email: '',
    selected_users: [],
  };

  let templates = Object.values(EMAIL_CAMPAIGN_TEMPLATES);
  let recipients_type = Object.values(EMAIL_CAMPAIGN_RECIPIENTS);
  const intl = useIntl();

  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [recipientsType, setRecipientsType] = useState(recipients_type[0]);

  const getErrorClass = (formikProps, fieldName) => {
    return formikProps && formikProps.touched && formikProps.touched[fieldName] ? formikProps.errors && formikProps.errors[fieldName] !== undefined ? 'custom-is-invalid' : '' : '';
  }

  const createEmailCampaign = async (values) => {
    if (values.recipients_type !== EMAIL_CAMPAIGN_RECIPIENTS_TYPES.TEST_USERS) {
      delete values.recipient_test_email;
    }

    if (values.recipients_type !== EMAIL_CAMPAIGN_RECIPIENTS_TYPES.SELECTED_USERS) {
      delete values.selected_users;
    }

    return createEmailCampaignApi({ data: values, notificationId: NOTIFICATION_CREATE_EMAIL_CAMPAIGN }).then(response => {
      if (response && response.status === HTTP_CREATED) {
        return true;
      }

      return false;
    });
  }

  const getUsersData = (searchString, callback) => {
    if (! ACL.CLIENT.USERS.LIST.hasAccess()) {
      return;
    }

    getUsersApi({
      pagination: {
        page: 1,
        q: searchString,
      },
      disablePagination: true,
      hideNotification: false,
      hideLoading: true,
    }).then((response) => {
      if (!response || !response.data) {
        return;
      }

      callback(response.data.map((item) => {
        return { value: item.id, label: `${item.first_name} ${item.last_name} ${item.email}` }
      }));
    });
  }

  return (
      <Formik
        key="create_email_campaign"
        initialValues={ initialValues }
        validationSchema={ CreateEmailCampaignSchema }
        validateOnChange={ true }
        enableReinitialize={ true }
        onSubmit={async (values, { resetForm }) => {

            let reset = await createEmailCampaign(values);

            if (reset) {
              resetForm({
                template: EMAIL_CAMPAIGN_TEMPLATES.TERMS_AND_POLICY_UPDATE.value,
                recipients_type: EMAIL_CAMPAIGN_RECIPIENTS_TYPES.TEST_USERS,
                recipient_test_email: '',
                selected_users: [],
              });

              setSelectedTemplate(templates[0]);
              setRecipientsType(recipients_type[0]);
              props.actionCallback();
            };
          }
        }
      >
        {
          formikProps => {
            return (
              <Form className="create-email-campaign-form">
                <Row className="create-email-campaign-select-row">
                  <Col className="create-email-campaign-field" xs={12} md={6}>
                    <Label>
                      <FormattedMessage id="components.pages.emailCampaigns.createEmailCampaign.createEmailCampaignForm.template"/>
                    </Label>
                    <SelectField
                      name="template"
                      className={`mb-4 ${getErrorClass(formikProps, 'template')}`}
                      classNamePrefix="ml-0"
                      defaultValue={templates[0]}
                      options={templates}
                      onChange={(selectedTemplate) => {
                        if (! selectedTemplate) {
                          return;
                        }

                        formikProps.setFieldValue('template', selectedTemplate.value);
                        setSelectedTemplate(selectedTemplate);
                      }
                      }
                      value={selectedTemplate}
                      formikProps={formikProps}
                      getErrorClass={getErrorClass}
                    />
                    <FieldMessages
                      color="danger"
                      formikProps={formikProps}
                      class="small-field-error"
                      showDismiss={false}
                      name="template"
                      notificationId={NOTIFICATION_CREATE_EMAIL_CAMPAIGN}
                    >
                      <ErrorMessage
                        name="template"
                        component="div"
                        data-testid="errors-template"
                        className="no-input-margin"
                      />
                    </FieldMessages>
                  </Col>
                  <Col className="create-email-campaign-field" xs={12} md={6}>
                    <Label>
                      <FormattedMessage id="components.pages.emailCampaigns.createEmailCampaign.createEmailCampaignForm.recipients"/>
                    </Label>
                    <SelectField
                      name="recipients_type"
                      className={`mb-4 ${getErrorClass(formikProps, 'recipients_type')}`}
                      classNamePrefix="ml-0"
                      defaultValue={recipients_type[0]}
                      options={recipients_type}
                      onChange={(selectedRecipientsType) => {
                        if (! selectedRecipientsType) {
                          return;
                        }

                        formikProps.setFieldValue('recipients_type', selectedRecipientsType.value);
                        setRecipientsType(selectedRecipientsType);

                        if (selectedRecipientsType.value !== EMAIL_CAMPAIGN_RECIPIENTS_TYPES.TEST_USERS) {
                          formikProps.setFieldValue('recipient_test_email', '');
                        }

                        if (selectedRecipientsType.value !== EMAIL_CAMPAIGN_RECIPIENTS_TYPES.SELECTED_USERS) {
                          formikProps.setFieldValue('selected_users', []);
                        }
                      }}
                      value={recipientsType}
                      formikProps={formikProps}
                      getErrorClass={getErrorClass}
                    />
                    <FieldMessages
                      color="danger"
                      formikProps={formikProps}
                      class="small-field-error"
                      showDismiss={false}
                      name="recipients"
                      notificationId={NOTIFICATION_CREATE_EMAIL_CAMPAIGN}
                    >
                      <ErrorMessage
                        name="recipients"
                        component="div"
                        data-testid="errors-recipients"
                        className="no-input-margin"
                      />
                    </FieldMessages>
                  </Col>
                </Row>

                <div className="create-email-campaign-conditional-field">
                    {
                      formikProps.values.recipients_type === EMAIL_CAMPAIGN_RECIPIENTS_TYPES.TEST_USERS &&
                      <>
                        <Label>
                          <FormattedMessage id="fields.recipient_test_email"/>
                        </Label>
                        <TextField
                          name="recipient_test_email"
                          handleBlur={formikProps.handleBlur}
                          onChange={
                            event => {
                              formikProps.handleChange(event);
                              formikProps.setFieldValue('recipient_test_email', event.target.value);

                              let state = store.getState();

                              if (state.notification.message && state.notification.message['recipient_test_email']) {
                                let message = state.notification.message;
                                delete message['recipient_test_email'];
                                this.props.showNotification(NOTIFICATION_ERROR, Object.keys(message).length ? message : null, NOTIFICATION_CREATE_EMAIL_CAMPAIGN);
                              }
                            }
                          }
                          value={formikProps.values.recipient_test_email}
                          errorClass={getErrorClass(formikProps, 'recipient_test_email')}
                        />
                        <FieldMessages
                          color="danger"
                          formikProps={formikProps}
                          class="small-field-error"
                          showDismiss={false}
                          name="recipient_test_email"
                          notificationId={NOTIFICATION_CREATE_EMAIL_CAMPAIGN}
                        >
                          <ErrorMessage
                            name="recipient_test_email"
                            component="div"
                            data-testid="errors-recipient_test_email"
                            className="no-input-margin"
                          />
                        </FieldMessages>
                      </>
                    }

                    {
                      formikProps.values.recipients_type === EMAIL_CAMPAIGN_RECIPIENTS_TYPES.SELECTED_USERS &&
                      <>
                        <Label>
                          <FormattedMessage id="components.pages.emailCampaigns.createEmailCampaign.createEmailCampaignForm.selected_user_emails"/>
                        </Label>
                        <UserMultiSelect
                          name="selected_users"
                          className={`mb-4 ${getErrorClass(formikProps, 'selected_users')}`}
                          value={formikProps.values.selected_users}
                          placeholder={ intl.formatMessage({ id: "components.pages.emailCampaigns.createEmailCampaign.createEmailCampaignForm.fields.selected_user_emails" }) }
                          onChange={(selectedOptions) => {
                            let newSelectedUsers = selectedOptions.map((option) => { return option.value });
                            formikProps.setFieldValue('selected_users', newSelectedUsers);
                          }}
                          loadOptions={getUsersData}
                          formikProps={formikProps}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                            menu: (base) => ({ ...base, overflowWrap: 'anywhere' }),
                          }}
                        />
                        <FieldMessages
                          color="danger"
                          formikProps={formikProps}
                          class="small-field-error"
                          showDismiss={false}
                          name="selected_users"
                          notificationId={NOTIFICATION_CREATE_EMAIL_CAMPAIGN}
                        >
                          <ErrorMessage
                            name="selected_users"
                            component="div"
                            data-testid="errors-selected_users"
                            className="no-input-margin"
                          />
                        </FieldMessages>
                      </>
                    }
                </div>

                <Row className="create-email-campaign-actions justify-content-end m-0">
                  <Button type="submit" color="primary">
                    <FormattedMessage id="button.send" />
                  </Button>
                </Row>
              </Form>
            );
          }
        }
      </Formik>
  )
}

export { CreateEmailCampaignForm };
